/**
 * Analytics utility for tracking user behavior, campaigns, and conversions.
 * 
 * This module provides a centralized way to:
 * - Capture and store UTM parameters from URLs
 * - Track page views and user interactions
 * - Log conversion events (signups, purchases)
 * - Send data to Google Analytics and Firestore
 * 
 * GDPR Compliance:
 * - All analytics tracking checks for user consent before firing
 * - Users can accept or reject optional cookies (analytics, marketing)
 * - Essential functionality works without consent
 * 
 * Usage:
 *   Analytics.captureUTMParams();  // Call on page load
 *   Analytics.trackPageView('Landing Page');
 *   Analytics.trackEvent('button_clicked', { button_name: 'Generate' });
 *   Analytics.trackConversion(firebase, userId, 'signup', { method: 'email' });
 */

class Analytics {
  /**
   * Check if user has consented to analytics cookies.
   * Returns true if consent is granted, false otherwise.
   * 
   * @returns {boolean} Whether user has consented to analytics
   */
  static hasConsent() {
    const cookieConsent = localStorage.getItem('cookieConsent');
    const legacyConsent = localStorage.getItem('cookiesAccepted');
    return cookieConsent === 'accepted' || legacyConsent === 'true';
  }
  /**
   * Capture UTM and custom tracking parameters from current URL.
   * Stores them in localStorage for attribution during conversions.
   * 
   * @returns {Object} Captured UTM and tracking data
   */
  static captureUTMParams() {
    const urlSearchParams = new URLSearchParams(window.location.search);
    
    const trackingData = {
      // Standard UTM parameters
      utm_source: urlSearchParams.get('utm_source'),
      utm_medium: urlSearchParams.get('utm_medium'),
      utm_campaign: urlSearchParams.get('utm_campaign'),
      utm_content: urlSearchParams.get('utm_content'),
      utm_term: urlSearchParams.get('utm_term'),
      
      // Custom parameters (existing)
      ref: urlSearchParams.get('ref'),
      aff: urlSearchParams.get('aff'),
      
      // Additional custom tracking
      feature_id: urlSearchParams.get('feature_id'),
      pack_id: urlSearchParams.get('pack_id'),
      placement: urlSearchParams.get('placement'),
      variant: urlSearchParams.get('variant'),
      
      captured_at: new Date().toISOString(),
    };

    // Store each parameter if it exists
    Object.entries(trackingData).forEach(([key, value]) => {
      if (value) {
        localStorage.setItem(key, value);
      }
    });

    return trackingData;
  }

  /**
   * Retrieve all stored attribution data from localStorage.
   * Used when tracking conversions to attribute them to campaigns.
   * 
   * @returns {Object} All stored attribution parameters
   */
  static getAttributionData() {
    return {
      utm_source: localStorage.getItem('utm_source'),
      utm_medium: localStorage.getItem('utm_medium'),
      utm_campaign: localStorage.getItem('utm_campaign'),
      utm_content: localStorage.getItem('utm_content'),
      utm_term: localStorage.getItem('utm_term'),
      ref: localStorage.getItem('ref'),
      aff: localStorage.getItem('aff'),
      feature_id: localStorage.getItem('feature_id'),
      pack_id: localStorage.getItem('pack_id'),
      placement: localStorage.getItem('placement'),
      variant: localStorage.getItem('variant'),
    };
  }

  /**
   * Track page view event.
   * Sends to Google Analytics and optionally to Firestore.
   * 
   * @param {string} pageName - Human-readable page name
   * @param {Object} additionalData - Extra properties to include
   * @returns {Object|null} Event data that was tracked, or null if no consent
   */
  static trackPageView(pageName, additionalData = {}) {
    if (!this.hasConsent()) {
      return null;
    }

    const eventData = {
      event_type: 'page_view',
      page_name: pageName,
      page_path: window.location.pathname,
      page_url: window.location.href,
      timestamp: new Date().toISOString(),
      ...additionalData,
    };

    // Send to Google Analytics (if configured)
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: pageName,
        page_path: window.location.pathname,
        page_location: window.location.href,
      });
    }

    // Send to Google Tag Manager (if configured)
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'page_view',
        pageName: pageName,
        ...eventData,
      });
    }

    console.log('[Analytics] Page View:', pageName);
    return eventData;
  }

  /**
   * Track user interaction events (clicks, form submissions, etc).
   * 
   * @param {string} eventName - Name of the event (e.g., 'button_clicked')
   * @param {Object} eventData - Additional event properties
   * @returns {Object|null} Full event data, or null if no consent
   */
  static trackEvent(eventName, eventData = {}) {
    if (!this.hasConsent()) {
      return null;
    }

    const fullEventData = {
      event_type: 'user_action',
      event_name: eventName,
      page_path: window.location.pathname,
      timestamp: new Date().toISOString(),
      ...eventData,
    };

    // Send to Google Analytics
    if (window.gtag) {
      window.gtag('event', eventName, eventData);
    }

    // Send to Google Tag Manager
    if (window.dataLayer) {
      window.dataLayer.push({
        event: eventName,
        ...fullEventData,
      });
    }

    console.log('[Analytics] Event:', eventName, fullEventData);
    return fullEventData;
  }

  /**
   * Save an event to Firestore for permanent storage and custom analysis.
   * 
   * @param {Object} firebase - Firebase instance
   * @param {string} userId - User ID (uid)
   * @param {Object} eventData - Event properties to store
   */
  static async saveEventToFirestore(firebase, userId, eventData) {
    try {
      const attribution = this.getAttributionData();
      
      await firebase.addAnalyticsEvent({
        uid: userId,
        ...eventData,
        ...attribution,
        createdAt: new Date().toISOString(),
      });
      
      console.log('[Analytics] Event saved to Firestore:', eventData.event_name);
    } catch (error) {
      console.error('[Analytics] Error saving event to Firestore:', error);
    }
  }

  /**
   * Track conversion events (signup, purchase, subscription).
   * Sends to GA and stores in Firestore with full attribution data.
   * 
   * Note: Conversions are always tracked to Firestore regardless of consent,
   * but only sent to Google Analytics if user has consented.
   * 
   * @param {Object} firebase - Firebase instance
   * @param {string} userId - User ID (uid)
   * @param {string} conversionType - Type: 'signup', 'purchase', 'subscription'
   * @param {Object} conversionData - Conversion details (value, currency, etc)
   * @returns {Object} Event data that was tracked
   */
  static async trackConversion(firebase, userId, conversionType, conversionData = {}) {
    const attribution = this.getAttributionData();
    const hasConsent = this.hasConsent();
    
    const eventData = {
      event_type: 'conversion',
      conversion_type: conversionType,
      ...conversionData,
      ...attribution,
      timestamp: new Date().toISOString(),
    };

    // Send to Google Analytics only if consent given
    if (hasConsent) {
      if (window.gtag) {
        window.gtag('event', 'conversion', {
          transaction_id: conversionData.transaction_id,
          value: conversionData.value || 0,
          currency: conversionData.currency || 'USD',
          conversion_type: conversionType,
        });
        
        // Also send as a named conversion event for better tracking
        window.gtag('event', conversionType, {
          ...conversionData,
        });
      }

      // Send to Google Tag Manager
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'conversion',
          conversionType: conversionType,
          ...eventData,
        });
      }
    } else {
      console.log('[Analytics] Conversion to GA blocked - no consent:', conversionType);
    }

    // Always save to Firestore for internal tracking (not subject to cookie consent)
    try {
      await firebase.addConversion({
        uid: userId,
        ...eventData,
      });
      
      console.log('[Analytics] Conversion tracked:', conversionType, eventData);
    } catch (error) {
      console.error('[Analytics] Error saving conversion:', error);
    }

    return eventData;
  }

  /**
   * Track feature usage for product analytics.
   * 
   * @param {Object} firebase - Firebase instance
   * @param {string} userId - User ID
   * @param {string} featureName - Name of the feature used
   * @param {Object} featureData - Additional feature context
   */
  static async trackFeatureUsage(firebase, userId, featureName, featureData = {}) {
    // Track to GA/GTM if consent given
    this.trackEvent('feature_used', {
      feature_name: featureName,
      ...featureData,
    });

    // Always save to Firestore for internal product analytics
    if (userId) {
      await this.saveEventToFirestore(firebase, userId, {
        event_name: 'feature_used',
        feature_name: featureName,
        ...featureData,
      });
    }
  }

  /**
   * Clear all stored attribution data.
   * Useful after conversion or when starting a new session.
   */
  static clearAttributionData() {
    const keys = [
      'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
      'ref', 'aff', 'feature_id', 'pack_id', 'placement', 'variant', 'captured_at'
    ];
    
    keys.forEach(key => localStorage.removeItem(key));
    console.log('[Analytics] Attribution data cleared');
  }
}

export default Analytics;

